/* src/components/pages/Extraerlibroventascli.jsx - Modificado el 16-02-2026 */
/* ------- scraping Async LibroVentas mensual Cliente on demand ------------ */

import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";

import {
  Box,
  Stack,
  TextField,
  Button,
  Typography,
  MenuItem,
  LinearProgress
} from "@mui/material";

import { DataGrid } from "@mui/x-data-grid";
import { esES } from "@mui/x-data-grid/locales";
import { CustomToolbar } from "../../components/datagrid/CustomToolbar";
import DataGridSkeleton from "../../components/datagrid/DataGridSkeleton";

import { RiFileSearchLine } from "react-icons/ri";

import api from "../../services/api.js";

import { useAuth } from "../../context/AuthContext";
import { useFeedback } from "../../context/FeedbackContext";

import { normalizeRut } from "../utils/rutUtils";
import { validarRut } from "../utils/rutValidator";

const Extraerlibroventascli = ({ modo }) => {
  const { user } = useAuth();
  const { showFeedback } = useFeedback();

  /* ===============================
     Estados principales
  =============================== */

  const [bitacora, setBitacora] = useState([]);
  const [loading, setLoading] = useState(false);

  /* Async Jobs */
  const [jobId, setJobId] = useState(null);

  /* Job trabajando */
  const scraperOcupado = jobId !== null;

  /* Progreso Async */
  const [progress, setProgress] = useState(null);
  const [progresoTexto, setProgresoTexto] = useState("");

  /* Periodo */
  const [ano, setAno] = useState(new Date().getFullYear());
  const [mes, setMes] = useState(1);

  /* Server Pagination */
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [rowCount, setRowCount] = useState(0);

  /* ===============================
     Validación Rut Cliente
  =============================== */

  const rutValido = useMemo(() => {
    if (!user?.usuario) return false;
    return validarRut(user.usuario);
  }, [user?.usuario]);

  /* ===============================
     Bitácora server-side
  =============================== */

  const fetchBitacora = useCallback(async () => {
    if (!rutValido) {
      showFeedback({
        message: 'El Rut ingresado no es válido',
        severity: 'error',
      });
      setBitacora([]);
      setRowCount(0);
      return;
    }

    setLoading(true);

    try {
      const RutCli = normalizeRut(user.usuario);

      const { data } = await api.post("/libroventas/getbitacora", {
        RutCli,
        page,
        pageSize,
      });

      if (data?.success) {
        setBitacora(data.rows || []);
        setRowCount(data.total || 0);
      } else {
        setBitacora([]);
        setRowCount(0);
      }
    } catch {
      showFeedback({
        message: "❌ No fue posible obtener la bitácora",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [rutValido, user?.usuario, page, pageSize, showFeedback]);

  /* llamar a cargar bitacora */
  useEffect(() => {
    fetchBitacora();
  }, [fetchBitacora]);

  /* ===============================
     Polling Async Scraping Job
  =============================== */
  useEffect(() => {
    if (!jobId) return;

    const interval = setInterval(async () => {
      try {
        const { data } = await api.get(`/extraerlibroventas/status/${jobId}`
        );

        /* Progreso live */
        if (data.progress && typeof data.progress === "object") {
          setProgress(data.progress);

          if (data.progress.message) {
            setProgresoTexto(data.progress.message);
          }
        }

        if (data.state === "active" && !progresoTexto) {
          setProgresoTexto("⏳ Iniciando la solicitud...");
        }

        /* Completed */
        if (data.state === "completed") {
          clearInterval(interval);

          setProgresoTexto("🎉 Solicitud completada");

          showFeedback({
            message: "✅ Extracción LibroVentas finalizada",
            severity: "success",
          });

          await fetchBitacora();

          setTimeout(() => {
            setProgress(null);
            setProgresoTexto("");
          }, 4000);

          setJobId(null);
          return;
        }

        /* Failed */
        if (data.state === "failed") {
          clearInterval(interval);

          setProgress(null);
          setProgresoTexto("");
          setJobId(null);

          showFeedback({
            message: "❌ Falló el scraping LibroVentas",
            severity: "error",
          });

          return;
        }
      } catch {
        clearInterval(interval);

        setProgress(null);
        setProgresoTexto("");
        setJobId(null);

        showFeedback({
          message: "❌ No fue posible consultar estado del Job",
          severity: "error",
        });
      }
    }, 2500);

    return () => clearInterval(interval);
  }, [jobId, progresoTexto, fetchBitacora, showFeedback]);

  /* ===============================
     Solicitar scraping async
  =============================== */

  const solicitarExtraccion = async () => {
    if (!rutValido) {
      showFeedback({
        message: "El Rut del cliente no es válido",
        severity: "error",
      });
      return;
    }

    try {
      const RutCli = normalizeRut(user.usuario);

      showFeedback({
        message: "⏳ Iniciando scraping LibroVentas...",
        severity: "info",
      });

      const { data } = await api.post("/extraerlibroventas/start", {
        RutCli,
        RutEnr: "",
        TipCli: 1,
        Tipoing: 1,
        Mesaconsultar: mes,
        Anoaconsultar: ano,
      });

      if (data?.success) {
        setProgress(null);
        setProgresoTexto("");
        setJobId(data.jobId);

        showFeedback({
          message: "📌 Proceso ejecutándose en segundo plano",
          severity: "info",
        });
      } else {
        showFeedback({
          message: data?.message || "No fue posible iniciar extracción",
          severity: "error",
        });
      }
    } catch {
      showFeedback({
        message: "❌ Error al iniciar scraping LibroVentas",
        severity: "error",
      });
    }
  };


  /* ===============================
     DataGrid Config
  =============================== */

  const columns = useMemo(
    () => [
      {
        field: "id",
        headerName: "Id",
        width: 90,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "fecha",
        headerName: "Fecha",
        width: 200,
        align: "center",
        headerAlign: "center",
        renderCell: (p) =>
          p.value ? new Date(p.value).toLocaleString("es-CL") : "—",
      },
      {
        field: "tipo",
        headerName: "Tipo",
        width: 120,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "rutcli",
        headerName: "Rut Cliente",
        width: 130,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "rutenr",
        headerName: "Rut Enrolado",
        width: 130,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "nombre",
        headerName: "Razon Social",
        width: 300,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "detalle",
        headerName: "Detalle",
        flex: 1,
        minWidth: 400,
      },
    ],
    []
  );


  /* ===============================
     Render
  =============================== */

  return (
    <Box sx={{ width: "100%", overflow: "visible" }}>
      {/* Barra controles */}
      <Stack
        direction="row"
        spacing={1}
        flexWrap="wrap"
        alignItems="center"
        sx={{
          mb: 1,
          flexShrink: 0,       // 👈 NO se colapsa
        }}
      >
        {/* Rut Cliente */}
        <TextField
          label="Rut"
          value={user?.usuario || ""}
          size="small"
          disabled
          slotProps={{
            inputLabel: { shrink: true },
          }}
          sx={{
            minWidth: 140,
            flexShrink: 0,
            "& .MuiInputBase-root": {
              backgroundColor: (theme) => theme.custom.ui.readonlyBg,
            },

            "& .MuiInputBase-input.Mui-disabled": {
              WebkitTextFillColor: (theme) => theme.custom.ui.readonlyText,
              textAlign: 'center',
              letterSpacing: '0.03em',
            },

            "& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-disabled": {
              color: (theme) => theme.custom.ui.readonlyLabel,
            },
          }}
        />

        {/* Nombre Cliente */}
        <TextField
          label="Cliente"
          value={user?.nombre || ""}
          size="small"
          disabled
          slotProps={{
            inputLabel: {
              shrink: true,
            },
          }}
          sx={{
            minWidth: 260,
            flexGrow: 1,
            flexShrink: 0,

            "& .MuiInputBase-root": {
              backgroundColor: (theme) => theme.custom.ui.readonlyBg,
            },

            "& .MuiInputBase-input.Mui-disabled": {
              WebkitTextFillColor: (theme) => theme.custom.ui.readonlyText,
            },

            "& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-disabled": {
              color: (theme) => theme.custom.ui.readonlyLabel,
            },
          }}
        />

        {/* Mes */}
        <TextField
          select
          label="Mes"
          disabled={scraperOcupado}
          value={mes}
          onChange={(e) => setMes(e.target.value)}
          size="small"
          sx={{ width: 90,
            height: '40px', // Altura del TextField
            '& .MuiOutlinedInput-root': { height: '40px' },
            '& .MuiSelect-select': {
              textAlign: 'center',
            }
           }}
        >
          {Array.from({ length: 12 }, (_, i) => (
            <MenuItem key={i + 1} value={i + 1}>
              {i + 1}
            </MenuItem>
          ))}
        </TextField>

        {/* Año */}
        <TextField
          select
          label="Año"
          disabled={scraperOcupado}
          value={ano}
          onChange={(e) => setAno(e.target.value)}
          size="small"
          sx={{ width: 110,
            height: '40px', // Altura del TextField
            '& .MuiOutlinedInput-root': { height: '40px' },
            '& .MuiSelect-select': {
              textAlign: 'center',
            }
           }}
        >
          {Array.from({ length: 7 }, (_, i) => (
            <MenuItem key={i} value={new Date().getFullYear() - i}>
              {new Date().getFullYear() - i}
            </MenuItem>
          ))}
        </TextField>

        {/* Boton Solicitar */}
        <Button
          variant="contained"
          startIcon={<RiFileSearchLine />}
          onClick={() => {
            if (scraperOcupado) return;
            setPage(0);
            setRowCount(0);
            solicitarExtraccion();
          }}
          sx={{
            minWidth: 110,
            height: 40,
            flexShrink: 0,

            backgroundColor: (theme) => theme.custom.ui.primaryAction,
            color: (theme) => theme.custom.ui.primaryActionText,

            '&:hover': {
              backgroundColor: (theme) => theme.custom.ui.primaryActionHover,
            },
          }}
        >
          {scraperOcupado ? "Procesando..." : "Solicitar"}
        </Button>

      </Stack>

      {/* Progreso Async */}
      {jobId && (
        <Box sx={{ mt: 1, mb: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {progresoTexto || "⏳ Ejecutando extracción..."}
          </Typography>

          {progress?.current && progress?.total && (
            <LinearProgress
              variant="determinate"
              value={(progress.current / progress.total) * 100}
              sx={{ mt: 0.5 }}
            />
          )}
        </Box>
      )}

      {/* DataGrid */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 200px)', // ajusta según tu layout
          minHeight: 0,
        }}
      >
        <DataGrid
          rows={bitacora}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loading}
          density="compact"
          rowHeight={38}
          headerHeight={40}
          disableRowSelectionOnClick

          paginationMode="server"
          rowCount={rowCount}
          pageSizeOptions={[10, 20, 50, 100]}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={({ page: newPage, pageSize: newPageSize }) => {
            const isPageSizeChange = newPageSize !== pageSize;

            const finalPage = isPageSizeChange ? 0 : newPage;

            setPage(finalPage);
            setPageSize(newPageSize);
          }}

          localeText={esES.components.MuiDataGrid.defaultProps.localeText}
          slots={{
            toolbar: CustomToolbar,
            loadingOverlay: DataGridSkeleton,
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 400 },
            },
          }}
          sx={{
            height: '100%',
            width: '100%',
            minWidth: 720,      // scroll horizontal cuando corresponde
            border: 'none',
            // accesibilidad / foco
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
              outline: 'none',
            },
          }}
        />
      </Box>

      <Typography variant="caption" color="text.secondary">
        {rowCount.toLocaleString("es-CL")} registros encontrados
      </Typography>
    </Box>
  );
};

export default Extraerlibroventascli;
